/** P9.3 "Your calls": the call of the year against the no-change plan (each measure called: band or number, hit or miss), and each changed family's expectation against what it moved. */
import type { ContentCatalog, DraftView, RoundReport } from "@bsim/contracts";
import { callRequiredIn, type DecisionFamily, type KpiId } from "@bsim/domain";
import { GxCard } from "../theme/primitives";
import { GxNotes } from "../theme/GxNotes";
import { GxPill } from "../theme/GxPill";
import { GxTable, type GxColumn, type GxRow } from "../theme/GxTable";
import { deskLabel } from "../decisions/labels";
import { forecastWhen, formatKpi, formatPrintedChange, lastYearWhose } from "../format";
import { sameAsLastYearMiss } from "@gxs/metric-defs";
import { BAND_LABELS } from "../format/labels";
import { plainLabel } from "./plainLabels";
import { noChangeCell, NO_CHANGE_PLAN_NOTE } from "./noChange";
import { FORECAST_GRADED_ON_RESULT } from "../format/basis";
import type { ReactNode } from "react";
import { FORECAST_KEY, beatsBaseline, forecastBasis, rationaleLine, rationaleVerdict } from "./derive";

/** A measure the player did not call is absent, never a miss (round 1 has no call). */
const calledRows = (report: RoundReport): RoundReport["forecastErrors"] => report.forecastErrors.filter((row) => row.scored);

/** One verdict per forecast, in one place: whether the year landed in the called band (or within the tolerance of the called number), and, separately, whether the call was closer than "same as last year". */
function verdictOf(row: RoundReport["forecastErrors"][number]): { readonly tone: "good" | "neutral"; readonly chip: string; readonly beat: string } {
  const kpi = row.kpi as KpiId;
  const miss = row.absErrorBp === null || row.absErrorBp === undefined ? null : formatPrintedChange(kpi, row.absErrorBp / 10_000, { signed: false });
  const baseline = formatPrintedChange(kpi, row.noChangeAbsErrorBp / 10_000, { signed: false });
  const closer = beatsBaseline(row);
  const beat = row.format === "band" && row.forecastBand === "flat" ? "The same call as \u2018same as last year\u2019" : closer ? `Your call was closer. ${sameAsLastYearMiss(baseline)}` : `Your call was not closer. ${sameAsLastYearMiss(baseline)}`;
  if (row.format === "band") return row.bandHit ? { tone: "good", chip: "Right band", beat } : row.directionHit ? { tone: "neutral", chip: "Right direction, wrong band", beat } : { tone: "neutral", chip: "Missed", beat };
  return row.withinTolerance === true ? { tone: "good", chip: "Within the tolerance", beat } : { tone: "neutral", chip: miss === null ? "Missed" : `Missed by ${miss}`, beat };
}

/**
 * The player's own actual for a called measure, on the basis its call is scored on (ADR-0074/0088): the year's average for a flow reading (the expectation row's `actualAvg`), the year-end reading for a stock.
 * `twinActual` is the no-change plan's (the forecast twin's) figure on that same basis, never the player's, so this is a different number from the No-change plan column.
 */
export function happenedOf(report: RoundReport, kpi: KpiId): { readonly value: number | undefined; readonly basis: string } {
  const average = report.attribution.expectation?.find((r) => r.kpi === kpi)?.actualAvg;
  return average !== undefined && forecastWhen(kpi).when === "yearAverage" ? { value: average, basis: "the year's average" } : { value: report.kpis[kpi], basis: "year-end" };
}

function CallOfTheYear({ report }: { readonly report: RoundReport }) {
  const rows = calledRows(report);
  if (rows.length === 0) return null;
  const columns: readonly GxColumn[] = [
    { key: "measure", label: "Measure" }, { key: "call", label: "Your call", kind: "number" }, { key: "last", label: "Last year's figure", kind: "number" }, { key: "twin", label: "No-change plan’s result (move)", kind: "number" }, { key: "happened", label: "What happened to your bank", kind: "number" }, { key: "result", label: "Result" },
  ];
  const tableRows: readonly GxRow[] = rows.map((row) => {
    const kpi = row.kpi as KpiId;
    const v = verdictOf(row);
    return {
      key: row.kpi, testId: `results.call.${row.kpi}`,
      cells: {
        measure: <>{plainLabel(kpi)}<small className="gx-terminal-why gx-faint">{forecastBasis(row.kpi)}</small></>,
        call: <span className="gx-call-cell"><span className="gx-call-fig">{row.format === "band" ? (row.forecastBand === null ? "—" : BAND_LABELS[row.forecastBand]) : row.forecastValue === null ? "—" : formatKpi(kpi, row.forecastValue)}</span></span>,
        last: <span className="gx-call-cell"><span className="gx-call-fig">{formatKpi(kpi, row.noChangeBaseline)}</span><small className="gx-terminal-why gx-faint">{lastYearWhose(kpi)}</small></span>,
        twin: <span className="gx-call-cell">{noChangeCell(row)}</span>,
        happened: (() => { const h = happenedOf(report, kpi); return <span className="gx-call-cell"><span className="gx-call-fig">{h.value === undefined ? "—" : formatKpi(kpi, h.value)}</span><small className="gx-terminal-why gx-faint">your bank, with your decisions; {h.basis}</small></span>; })(),
        result: <span className="gx-call-cell"><GxPill tone={v.tone}>{v.chip}</GxPill><small className="gx-terminal-why gx-faint">{v.beat}</small></span>,
      },
    };
  });
  return (
    <div data-testid="results.calls">
      <GxTable testId="results.calls.table" caption="Your forecast against what happened" columns={columns} rows={tableRows} />
      <GxNotes testId="results.calls.notes" notes={[
        <span key="k" data-testid="results.calls.key">{FORECAST_KEY}</span>,
        <span key="b" data-testid="results.calls.basis">{FORECAST_GRADED_ON_RESULT}</span>,
      ]} />
      <p className="gx-small gx-faint" data-testid="results.calls.twin">{NO_CHANGE_PLAN_NOTE}</p>
    </div>
  );
}

function Rationales({ report, draft, catalog }: { readonly report: RoundReport; readonly draft: DraftView; readonly catalog: ContentCatalog }) {
  const rows = Object.entries(draft.rationales).flatMap(([family, r]) => {
    if (r === undefined) return [];
    const delta = report.familyReverts.find((f) => f.family === family)?.kpiDeltas[r.expected.kpi];
    const label = deskLabel(catalog, family as DecisionFamily);
    return [{ family, line: rationaleLine(label, r.expected.kpi, r.expected.direction, delta, report.attribution.vsAutopilot[r.expected.kpi]), verdict: rationaleVerdict(r.expected.kpi, r.expected.direction, delta) }];
  });
  if (rows.length === 0) return null;
  return (
    <div className="gx-results-rationales" data-testid="results.rationales">
      <h3 className="gx-h3">What you expected your changes to do</h3>
      <p className="gx-small gx-faint" data-testid="results.rationales.basis">Each verdict compares what you expected with how much the measure actually moved because of your changes.</p>
      {rows.map((r) => (
        <div key={r.family} className="gx-results-call" data-testid={`results.rationale.${r.family}`}>
          {r.verdict === null ? null : <GxPill tone={r.verdict.tone} label={r.verdict.chip}>{r.verdict.mark}</GxPill>}
          <span className="gx-small">{r.line}</span>
        </div>
      ))}
    </div>
  );
}

/** True when the year has no call to compare and no expectation to check: the card then carries only the round's lesson (and the legs that cancelled), under a title that says so. */
export const callsEmpty = (report: RoundReport, draft: DraftView | null): boolean => calledRows(report).length === 0 && (draft === null || Object.values(draft.rationales).every((r) => r === undefined));

/** `lead` and `foot` are the page's own sentences about the year's calls (the legs that cancelled, the round's lesson); they keep the card on the page when the player made no call. */
export function Calls({ report, draft, catalog, lead = null, foot = null, title = "Your forecasts" }: { readonly report: RoundReport; readonly draft: DraftView | null; readonly catalog: ContentCatalog; readonly lead?: ReactNode; readonly foot?: ReactNode; readonly title?: string }) {
  if (callsEmpty(report, draft) && lead === null && foot === null) return null;
  return (
    <GxCard title={title} testId="results.callscard">
      <div className="gx-card-b">
        {lead}
        {callRequiredIn(report.roundNo) ? <CallOfTheYear report={report} /> : null}
        {draft === null ? null : <Rationales report={report} draft={draft} catalog={catalog} />}
        {foot}
      </div>
    </GxCard>
  );
}
